import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Link2, Upload, Trash2, ExternalLink, FileText, Wand2, Lightbulb, MapPin } from "lucide-react";

const fileToDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export default function ClubResources() {
  const [items, setItems] = useState([]);
  const [activityLabel, setActivityLabel] = useState("");
  const [loading, setLoading] = useState(true);

  // Suggestions IA (non enregistrées)
  const [focus, setFocus] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Ajout de lien
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "" });
  const [addingLink, setAddingLink] = useState(false);

  // Ajout de fichier
  const [fileTitle, setFileTitle] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clubs/me/resources");
      setItems(data.items || []);
      setActivityLabel(data.activity_label || "");
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/clubs/me/resources/ai-suggest", { focus });
      setSuggestions(data.suggestions || []);
      setActivityLabel(data.activity_label || activityLabel);
      if (!(data.suggestions || []).length) toast.info("Aucune suggestion pour le moment, réessayez.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestions IA indisponibles, réessayez.");
    } finally { setSuggesting(false); }
  };

  const addLink = async (e) => {
    e.preventDefault();
    if (!linkForm.title.trim() || !linkForm.url.trim()) { toast.error("Titre et lien obligatoires."); return; }
    setAddingLink(true);
    try {
      await api.post("/clubs/me/resources/link", linkForm);
      toast.success("Lien ajouté !");
      setLinkForm({ title: "", url: "", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible."); }
    finally { setAddingLink(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 15 Mo)."); return; }
    setFileObj(f);
    if (!fileTitle.trim()) setFileTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const addFile = async (e) => {
    e.preventDefault();
    if (!fileObj) { toast.error("Sélectionnez un fichier."); return; }
    if (!fileTitle.trim()) { toast.error("Donnez un titre au fichier."); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(fileObj);
      await api.post("/clubs/me/resources/file", {
        title: fileTitle.trim(),
        filename: fileObj.name,
        content_base64: dataUrl,
        content_type: fileObj.type || "application/octet-stream",
      });
      toast.success("Fichier ajouté !");
      setFileTitle(""); setFileObj(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setUploading(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer « ${r.title} » ?`)) return;
    try { await api.delete(`/clubs/me/resources/${r.id}`); toast.success("Ressource supprimée."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
  };

  const links = items.filter((r) => r.kind === "link");
  const files = items.filter((r) => r.kind === "file");

  return (
    <div className="max-w-4xl" data-testid="club-resources">
      <div className="flex items-center gap-2">
        <Sparkles className="text-indigo-700" size={22} />
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Ressources par activité</h1>
      </div>
      <p className="text-slate-500 text-sm mt-1">
        Centralisez tout ce qui aide à faire vivre votre club : suggestions de l'IA{activityLabel ? ` pour « ${activityLabel} »` : ""}, liens utiles et documents à télécharger.
      </p>

      {/* SUGGESTIONS IA */}
      <section className="mt-6 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-2xl p-5" data-testid="club-resources-ai">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white grid place-items-center shrink-0"><Wand2 size={20} /></div>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-slate-900">Suggestions intelligentes</h2>
            <p className="text-sm text-slate-600 mt-0.5">
              Laissez l'IA vous proposer des ressources concrètes et adaptées à votre activité (fédérations, aides, matériel, encadrement…).
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder="Préciser un besoin (facultatif) : ex. « jeunes », « compétition », « subventions »…"
                data-testid="club-resources-ai-focus"
              />
              <Button onClick={suggest} disabled={suggesting} className="btn-shine text-white shrink-0" data-testid="club-resources-ai-btn">
                <Sparkles size={16} className="mr-1.5" /> {suggesting ? "Génération…" : "Suggérer des ressources"}
              </Button>
            </div>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="club-resources-ai-list">
            {suggestions.map((s, i) => (
              <div key={i} className="bg-white border border-indigo-100 rounded-xl p-4" data-testid={`club-resources-ai-item-${i}`}>
                <div className="flex items-start gap-2">
                  <Lightbulb size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 leading-snug">{s.title}</div>
                    {s.category && <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700">{s.category}</span>}
                    {s.description && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.description}</p>}
                    {s.where && <p className="mt-1.5 text-xs text-slate-500 flex items-start gap-1"><MapPin size={12} className="mt-0.5 shrink-0" /> {s.where}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* LIENS UTILES */}
      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="club-resources-links">
        <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Link2 size={18} className="text-indigo-700" /> Liens utiles</h2>
        <form onSubmit={addLink} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div><Label>Titre *</Label><Input value={linkForm.title} onChange={(e) => setLinkForm((s) => ({ ...s, title: e.target.value }))} placeholder="Ex. Fédération française…" data-testid="club-resources-link-title" /></div>
          <div><Label>Lien *</Label><Input value={linkForm.url} onChange={(e) => setLinkForm((s) => ({ ...s, url: e.target.value }))} placeholder="https://…" data-testid="club-resources-link-url" /></div>
          <Button type="submit" disabled={addingLink} className="btn-shine text-white" data-testid="club-resources-link-add">{addingLink ? "Ajout…" : "Ajouter"}</Button>
          <div className="md:col-span-3"><Label>Description (facultatif)</Label><Input value={linkForm.description} onChange={(e) => setLinkForm((s) => ({ ...s, description: e.target.value }))} placeholder="À quoi sert ce lien ?" data-testid="club-resources-link-desc" /></div>
        </form>

        <div className="mt-4 space-y-2">
          {links.length === 0 && <p className="text-sm text-slate-400">Aucun lien pour le moment.</p>}
          {links.map((r) => (
            <div key={r.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-3" data-testid={`club-resources-link-item-${r.id}`}>
              <Link2 size={16} className="text-indigo-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{r.title}</div>
                {r.description && <div className="text-xs text-slate-500 truncate">{r.description}</div>}
              </div>
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-indigo-700" title="Ouvrir"><ExternalLink size={16} /></a>
              <button onClick={() => remove(r)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`club-resources-link-del-${r.id}`} title="Supprimer"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </section>

      {/* DOCUMENTS */}
      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="club-resources-files">
        <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Upload size={18} className="text-indigo-700" /> Documents & fichiers</h2>
        <form onSubmit={addFile} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div><Label>Titre *</Label><Input value={fileTitle} onChange={(e) => setFileTitle(e.target.value)} placeholder="Ex. Règlement intérieur" data-testid="club-resources-file-title" /></div>
          <div><Label>Fichier * (max 15 Mo)</Label><Input ref={fileRef} type="file" onChange={onPickFile} data-testid="club-resources-file-input" /></div>
          <Button type="submit" disabled={uploading} className="btn-shine text-white" data-testid="club-resources-file-add">{uploading ? "Envoi…" : "Ajouter"}</Button>
        </form>

        <div className="mt-4 space-y-2">
          {files.length === 0 && <p className="text-sm text-slate-400">Aucun document pour le moment.</p>}
          {files.map((r) => (
            <div key={r.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-3" data-testid={`club-resources-file-item-${r.id}`}>
              <FileText size={16} className="text-indigo-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{r.title}</div>
                <div className="text-xs text-slate-500 truncate">{r.filename}{r.size ? ` · ${Math.round(r.size / 1024)} Ko` : ""}</div>
              </div>
              <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-indigo-700" title="Télécharger"><ExternalLink size={16} /></a>
              <button onClick={() => remove(r)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`club-resources-file-del-${r.id}`} title="Supprimer"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </section>

      {loading && <div className="mt-4 text-sm text-slate-400">Chargement…</div>}
    </div>
  );
}
